import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PredictionCard from './components/PredictionCard';
import PredictionChart from './components/PredictionChart';
import AccuracyStats from './components/AccuracyStats';
import MarketAnalysis from './components/MarketAnalysis';
import RelatedPredictions from './components/RelatedPredictions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { generatePricePrediction, generateMarketAnalysis } from '../../services/openaiServices';

const AIPredictionsDetail = () => {
  const [user] = useState({
    name: "Alex Thompson",
    email: "alex.thompson@email.com",
    subscriptionTier: "pro"
  });

  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [accuracyData] = useState([
    { date: "Week 1", accuracy: 89 },
    { date: "Week 2", accuracy: 92 },
    { date: "Week 3", accuracy: 85 },
    { date: "Week 4", accuracy: 91 }
  ]);

  const [performanceMetrics] = useState({
    successRate: 89,
    avgDeviation: 4.2,
    totalPredictions: 247,
    avgTimeframe: "5.2 days",
    highAccuracy: 65,
    mediumAccuracy: 28,
    lowAccuracy: 7
  });

  const [analysisData, setAnalysisData] = useState({
    sentiment: {
      overall: "Bullish",
      strength: 78,
      social: "Bullish",
      socialScore: 82,
      news: "Neutral",
      newsScore: 65
    },
    technicalIndicators: [
      {
        name: "RSI (14)",
        description: "Relative Strength Index",
        signal: "Bullish",
        value: "58.4"
      },
      {
        name: "MACD",
        description: "Moving Average Convergence Divergence",
        signal: "Bullish",
        value: "Positive crossover"
      },
      {
        name: "Support/Resistance",
        description: "Key price levels",
        signal: "Neutral",
        value: "Support at $65,000"
      },
      {
        name: "Volume Analysis",
        description: "Trading volume trends",
        signal: "Bullish",
        value: "Above average"
      }
    ],
    keyFactors: [
      {
        title: "Institutional Adoption",
        description: "Major corporations continue to add Bitcoin to their treasury reserves, creating sustained demand.",
        impact: "Positive"
      },
      {
        title: "Regulatory Clarity",
        description: "Recent regulatory developments provide clearer framework for cryptocurrency operations.",
        impact: "Positive"
      },
      {
        title: "Market Volatility",
        description: "Increased volatility around key resistance levels may create short-term uncertainty.",
        impact: "Negative"
      },
      {
        title: "Global Economic Conditions",
        description: "Macroeconomic factors including inflation concerns support alternative store of value narrative.",
        impact: "Positive"
      }
    ],
    correlations: [
      { asset: "Ethereum (ETH)", value: 0.85 },
      { asset: "S&P 500", value: 0.42 },
      { asset: "Gold", value: -0.23 },
      { asset: "US Dollar Index", value: -0.67 }
    ]
  });

  const [relatedPredictions, setRelatedPredictions] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [authenticationError, setAuthenticationError] = useState(null);
  const [retryAttempts, setRetryAttempts] = useState(0);

  // Mock historical data for AI analysis
  const mockHistoricalData = [
    { date: "Jan 1", price: 65000, prediction: null },
    { date: "Jan 2", price: 66200, prediction: null },
    { date: "Jan 3", price: 65800, prediction: 67000 },
    { date: "Jan 4", price: 67100, prediction: 68200 },
    { date: "Jan 5", price: 67420, prediction: 69500 },
    { date: "Jan 6", price: null, prediction: 70800 },
    { date: "Jan 7", price: null, prediction: 72500 }
  ];

  // Enhanced API key validation function
  const validateApiKeyConfiguration = () => {
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    
    const validation = {
      hasKey: !!apiKey,
      isPlaceholder: !apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.includes('your-'),
      isValidFormat: apiKey?.startsWith('sk-') && apiKey?.length > 20,
      keyPrefix: apiKey?.substring(0, 15) + '...' || 'Not configured'
    };

    validation.isValid = validation?.hasKey && !validation?.isPlaceholder && validation?.isValidFormat;
    
    setApiKeyStatus(validation);
    return validation;
  };

  // Enhanced authentication test function
  const testOpenAIAuthentication = async () => {
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env?.VITE_OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        timeout: 8000 // 8 second timeout
      });

      if (response?.ok) {
        setAuthenticationError(null);
        return { success: true, message: 'Authentication successful' };
      } else if (response?.status === 401) {
        const error = { 
          success: false, 
          status: 401, 
          message: 'API key is invalid or expired',
          suggestions: [
            'Check your API key at https://platform.openai.com/api-keys',
            'Ensure billing is set up and credits are available',
            'Try regenerating a new API key',
            'Verify the key was copied correctly without extra spaces'
          ]
        };
        setAuthenticationError(error);
        return error;
      } else if (response?.status === 429) {
        const error = { 
          success: false, 
          status: 429, 
          message: 'API quota exceeded or rate limit reached',
          suggestions: [
            'Check your billing and usage at https://platform.openai.com/account/billing',
            'Upgrade your OpenAI plan if needed',
            'Wait a few minutes before retrying'
          ]
        };
        setAuthenticationError(error);
        return error;
      } else {
        const error = { 
          success: false, 
          status: response?.status, 
          message: 'Authentication test failed',
          suggestions: ['Check your network connection and try again']
        };
        setAuthenticationError(error);
        return error;
      }
    } catch (error) {
      const authError = { 
        success: false, 
        message: 'Network error during authentication test',
        error: error?.message,
        suggestions: ['Check your internet connection', 'Verify firewall settings']
      };
      setAuthenticationError(authError);
      return authError;
    }
  };

  // Load AI-generated prediction on component mount
  useEffect(() => {
    validateApiKeyConfiguration();
    loadAIPrediction();
    loadAIRelatedPredictions();
  }, []);

  // Enhanced loadAIPrediction function with better real-time market data integration
  const loadAIPrediction = async () => {
    const keyValidation = validateApiKeyConfiguration();
    
    if (!keyValidation?.isValid) {
      // Use fallback mock data if API key is not configured properly
      setSelectedPrediction({
        id: 1,
        name: "Bitcoin",
        symbol: "BTC",
        currentPrice: 67420,
        change24h: 2.34,
        confidence: 87,
        priceTarget: 72500,
        targetRange: { min: 70000, max: 75000 },
        timeframe: "7 Days",
        riskLevel: "Medium",
        analysis: keyValidation?.isPlaceholder 
          ? `OpenAI API key not configured. Please add your API key to enable real-time AI predictions.`
          : keyValidation?.hasKey 
            ? `API key format invalid. Please ensure your API key starts with "sk-" and is complete.`
            : `No OpenAI API key found. Please configure VITE_OPENAI_API_KEY in your .env file.`,
        fallback: true,
        errorType: keyValidation?.isPlaceholder ? 'MISSING_API_KEY' : 'INVALID_API_KEY',
        reason: 'API key configuration issue',
        configurationRequired: true
      });
      setChartData(mockHistoricalData);
      setPredictions([
        { date: "Jan 6", target: 70800, accuracy: 92 },
        { date: "Jan 7", target: 72500, accuracy: null }
      ]);
      return;
    }

    setLoading(true);
    try {
      // Test authentication first with improved error handling
      const authTest = await testOpenAIAuthentication();
      if (!authTest?.success) {
        // Don't throw error, just log and use fallback console.warn('OpenAI authentication failed, using fallback data:', authTest?.message);
        
        // Set authentication error state for UI feedback
        setAuthenticationError(authTest);
        setAlertMessage(`Authentication failed: ${authTest?.message || 'API key is invalid or expired'}`);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 8000);
        
        // Use enhanced fallback instead of throwing error
        const currentPrice = 68150;
        setSelectedPrediction({
          id: 1,
          name: "Bitcoin",
          symbol: "BTC",
          currentPrice: currentPrice,
          change24h: 1.08,
          confidence: 87,
          priceTarget: 72500,
          targetRange: { min: 70000, max: 75000 },
          timeframe: "7 Days",
          riskLevel: "Medium",
          analysis: `Authentication Error: ${authTest?.message || 'API key is invalid or expired'}\n\nFallback Analysis: Based on recent market patterns and technical indicators, Bitcoin shows moderate bullish sentiment with key support levels around $65,000. Current market conditions suggest cautious optimism with proper risk management strategies. The current price of $${currentPrice?.toLocaleString()} indicates healthy momentum.`,
          fallback: true,
          errorType: 'AUTHENTICATION_ERROR',
          reason: authTest?.message || 'Authentication failed',
          configurationRequired: true,
          keyFactors: [
            'Technical analysis shows bullish momentum patterns',
            'Support levels holding strong around $65,000',
            'Market sentiment remains cautiously optimistic'
          ]
        });
        
        const enhancedChartData = [
          { date: new Date(Date.now() - 6*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65000, prediction: null },
          { date: new Date(Date.now() - 5*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 66200, prediction: null },
          { date: new Date(Date.now() - 4*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65800, prediction: 67000 },
          { date: new Date(Date.now() - 3*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67100, prediction: 68200 },
          { date: new Date(Date.now() - 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67420, prediction: 69500 },
          { date: new Date(Date.now() - 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 68150, prediction: 70800 },
          { date: new Date(Date.now())?.toISOString()?.split('T')?.[0], price: null, prediction: 72500 }
        ];
        
        setChartData(enhancedChartData);
        setPredictions([
          { date: new Date(Date.now() + 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], target: 70800, accuracy: 92 },
          { date: new Date(Date.now() + 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], target: 72500, accuracy: null }
        ]);
        
        setLoading(false);
        return; // Return early with fallback data
      }

      // Enhanced historical data with more recent and realistic data points
      const enhancedHistoricalData = [
        { date: new Date(Date.now() - 6*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65000 },
        { date: new Date(Date.now() - 5*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 66200 },
        { date: new Date(Date.now() - 4*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65800 },
        { date: new Date(Date.now() - 3*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67100 },
        { date: new Date(Date.now() - 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67420 },
        { date: new Date(Date.now() - 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 68150 },
      ];

      const prediction = await generatePricePrediction('BTC', enhancedHistoricalData);
      
      // Enhanced error handling for quota exceeded with better messaging
      if (prediction?.fallback) {
        console.warn('Using fallback prediction due to API limitations:', prediction?.error);
        
        // Show enhanced user-friendly message
        const message = prediction?.reason || prediction?.errorMessage || 'AI prediction temporarily unavailable';
          
        setAlertMessage(message);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 10000); // Longer display time for errors
      }
      
      // Enhanced market data with current timestamp and realistic values
      const enhancedMarketData = {
        currentPrice: prediction?.currentPrice || 68150,
        change24h: ((prediction?.currentPrice || 68150) - 67420) / 67420 * 100,
        volume24h: 28500000000 + (Math.random() * 5000000000), // Dynamic volume
        marketCap: (prediction?.currentPrice || 68150) * 19700000, // Current circulating supply
        timestamp: new Date()?.toISOString(),
      };
      
      const analysis = await generateMarketAnalysis('BTC', enhancedMarketData, 'crypto');
      
      // Check if analysis also used fallback
      if (analysis?.fallback && analysis?.reason) {
        console.warn('Market analysis also using fallback due to limitations:', analysis?.reason);
      }

      // Enhanced chart data with predictions
      const enhancedChartData = [
        ...enhancedHistoricalData?.map(d => ({ date: d?.date, price: d?.price, prediction: null })),
        { 
          date: new Date(Date.now() + 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], 
          price: null, 
          prediction: prediction?.predictions?.[1]?.priceTarget || 70800 
        },
        { 
          date: new Date(Date.now() + 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], 
          price: null, 
          prediction: prediction?.predictions?.[0]?.priceTarget || 72500 
        }
      ];

      setSelectedPrediction({
        id: 1,
        name: "Bitcoin",
        symbol: prediction?.symbol || "BTC",
        currentPrice: enhancedMarketData?.currentPrice,
        change24h: enhancedMarketData?.change24h,
        confidence: prediction?.predictions?.[0]?.confidence || 87,
        priceTarget: prediction?.predictions?.[0]?.priceTarget || 72500,
        targetRange: { 
          min: (prediction?.predictions?.[0]?.priceTarget || 72500) * 0.95, 
          max: (prediction?.predictions?.[0]?.priceTarget || 72500) * 1.05 
        },
        timeframe: prediction?.predictions?.[0]?.timeframe || "7 Days",
        riskLevel: prediction?.riskLevel || "Medium",
        analysis: prediction?.analysis || analysis?.analysis || "AI-generated analysis based on current market conditions and technical indicators.",
        fallback: prediction?.fallback || analysis?.fallback || false,
        errorType: prediction?.error?.type || analysis?.error?.type,
        reason: prediction?.reason || analysis?.reason || 'Unknown',
        keyFactors: prediction?.keyFactors || [
          'Recent price momentum showing bullish signals',
          'Market sentiment analysis indicates optimistic outlook',
          'Technical indicators support continued upward movement'
        ]
      });

      setChartData(enhancedChartData);
      setPredictions([
        { 
          date: new Date(Date.now() + 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], 
          target: prediction?.predictions?.[1]?.priceTarget || 70800, 
          accuracy: 92 
        },
        { 
          date: new Date(Date.now() + 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], 
          target: prediction?.predictions?.[0]?.priceTarget || 72500, 
          accuracy: null 
        }
      ]);

    } catch (error) {
      console.error('Error loading AI prediction:', error);
      
      // Enhanced error message handling with better user feedback
      let errorMessage = 'AI prediction failed. Showing fallback analysis.';
      let reason = 'Unknown error';
      let errorType = 'GENERAL_ERROR';
      
      if (error?.message?.includes('Authentication failed') || error?.status === 401) {
        errorMessage = 'OpenAI API authentication failed. Your API key is invalid or expired.';
        reason = 'Authentication failed';
        errorType = 'AUTHENTICATION_ERROR';
      } else if (error?.status === 429) {
        errorMessage = 'OpenAI quota exceeded. Please add billing credits and try again.';
        reason = 'API quota exceeded';
        errorType = 'RATE_LIMIT_EXCEEDED';
      } else if (error?.message?.includes('quota')) {
        errorMessage = 'API quota limits reached. Using fallback predictions.';
        reason = 'Quota limits reached';
        errorType = 'RATE_LIMIT_EXCEEDED';
      } else if (error?.message?.includes('network')) {
        errorMessage = 'Network connection failed. Using fallback predictions.';
        reason = 'Network error';
        errorType = 'NETWORK_ERROR';
      }
        
      setAlertMessage(errorMessage);
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 10000);
      
      // Fallback to enhanced mock data on error
      const currentPrice = 68150;
      setSelectedPrediction({
        id: 1,
        name: "Bitcoin",
        symbol: "BTC",
        currentPrice: currentPrice,
        change24h: 1.08,
        confidence: 87,
        priceTarget: 72500,
        targetRange: { min: 70000, max: 75000 },
        timeframe: "7 Days",
        riskLevel: "Medium",
        analysis: `${errorMessage}\n\nFallback Analysis: Based on recent market patterns and technical indicators, Bitcoin shows moderate bullish sentiment with key support levels around $65,000. Current market conditions suggest cautious optimism with proper risk management strategies. The current price of $${currentPrice?.toLocaleString()} indicates healthy momentum.`,
        fallback: true,
        errorType: errorType,
        reason: reason,
        configurationRequired: errorType === 'AUTHENTICATION_ERROR',
        keyFactors: [
          'Technical analysis shows bullish momentum patterns',
          'Support levels holding strong around $65,000',
          'Market sentiment remains cautiously optimistic'
        ]
      });
      
      const enhancedChartData = [
        { date: new Date(Date.now() - 6*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65000, prediction: null },
        { date: new Date(Date.now() - 5*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 66200, prediction: null },
        { date: new Date(Date.now() - 4*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 65800, prediction: 67000 },
        { date: new Date(Date.now() - 3*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67100, prediction: 68200 },
        { date: new Date(Date.now() - 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 67420, prediction: 69500 },
        { date: new Date(Date.now() - 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], price: 68150, prediction: 70800 },
        { date: new Date(Date.now())?.toISOString()?.split('T')?.[0], price: null, prediction: 72500 }
      ];
      
      setChartData(enhancedChartData);
      setPredictions([
        { date: new Date(Date.now() + 1*24*60*60*1000)?.toISOString()?.split('T')?.[0], target: 70800, accuracy: 92 },
        { date: new Date(Date.now() + 2*24*60*60*1000)?.toISOString()?.split('T')?.[0], target: 72500, accuracy: null }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced loadAIRelatedPredictions function with better error messaging
  const loadAIRelatedPredictions = async () => {
    setLoadingRelated(true);
    try {
      const relatedAssets = ['ETH', 'ADA', 'SOL'];
      const aiRelatedPredictions = [];

      // Generate AI predictions for related cryptocurrencies
      for (const asset of relatedAssets) {
        try {
          // Mock historical data for each asset
          const mockData = {
            ETH: [
              { date: '2025-01-01', price: 3800 },
              { date: '2025-01-02', price: 3920 },
              { date: '2025-01-03', price: 3850 },
              { date: '2025-01-04', price: 4050 },
              { date: '2025-01-05', price: 4100 },
            ],
            ADA: [
              { date: '2025-01-01', price: 0.65 },
              { date: '2025-01-02', price: 0.67 },
              { date: '2025-01-03', price: 0.66 },
              { date: '2025-01-04', price: 0.69 },
              { date: '2025-01-05', price: 0.68 },
            ],
            SOL: [
              { date: '2025-01-01', price: 140 },
              { date: '2025-01-02', price: 143 },
              { date: '2025-01-03', price: 142 },
              { date: '2025-01-04', price: 146 },
              { date: '2025-01-05', price: 145 },
            ]
          };

          const historicalData = mockData?.[asset] || [];
          const prediction = await generatePricePrediction(asset, historicalData);
          
          if (prediction && prediction?.predictions && prediction?.predictions?.length > 0) {
            const assetNames = {
              ETH: 'Ethereum',
              ADA: 'Cardano', 
              SOL: 'Solana'
            };

            aiRelatedPredictions?.push({
              id: aiRelatedPredictions?.length + 2,
              name: assetNames?.[asset] || asset,
              symbol: asset,
              priceTarget: prediction?.predictions?.[0]?.priceTarget,
              timeframe: prediction?.predictions?.[0]?.timeframe || "5-7 Days",
              trend: prediction?.predictions?.[0]?.trend || "Neutral",
              confidence: prediction?.predictions?.[0]?.confidence || 70,
              fallback: prediction?.fallback || false,
              reason: prediction?.reason || null
            });
          }
        } catch (error) {
          console.warn(`Failed to generate AI prediction for ${asset}:`, error?.message || error);
          // Add fallback prediction for this asset
          const fallbackPredictions = {
            ETH: { name: "Ethereum", priceTarget: 4200, trend: "Bullish", confidence: 82 },
            ADA: { name: "Cardano", priceTarget: 0.68, trend: "Neutral", confidence: 71 },
            SOL: { name: "Solana", priceTarget: 145, trend: "Bullish", confidence: 79 }
          };

          if (fallbackPredictions?.[asset]) {
            aiRelatedPredictions?.push({
              id: aiRelatedPredictions?.length + 2,
              name: fallbackPredictions?.[asset]?.name,
              symbol: asset,
              priceTarget: fallbackPredictions?.[asset]?.priceTarget,
              timeframe: "5-7 Days",
              trend: fallbackPredictions?.[asset]?.trend,
              confidence: fallbackPredictions?.[asset]?.confidence,
              fallback: true,
              reason: 'API call failed'
            });
          }
        }
      }

      setRelatedPredictions(aiRelatedPredictions?.length > 0 ? aiRelatedPredictions : [
        // Final fallback if all AI calls fail
        {
          id: 2,
          name: "Ethereum",
          symbol: "ETH",
          priceTarget: 4200,
          timeframe: "5 Days",
          trend: "Bullish",
          confidence: 82,
          fallback: true,
          reason: 'Service unavailable'
        },
        {
          id: 3,
          name: "Cardano",
          symbol: "ADA",
          priceTarget: 0.68,
          timeframe: "10 Days",
          trend: "Neutral",
          confidence: 71,
          fallback: true,
          reason: 'Service unavailable'
        },
        {
          id: 4,
          name: "Solana",
          symbol: "SOL",
          priceTarget: 145,
          timeframe: "7 Days",
          trend: "Bullish",
          confidence: 79,
          fallback: true,
          reason: 'Service unavailable'
        }
      ]);

    } catch (error) {
      console.error('Error loading AI related predictions:', error?.message || error);
      // Keep existing fallback data with enhanced error info
      setRelatedPredictions([
        {
          id: 2,
          name: "Ethereum",
          symbol: "ETH",
          priceTarget: 4200,
          timeframe: "5 Days",
          trend: "Bullish",
          confidence: 82,
          fallback: true,
          reason: 'Bulk prediction failed'
        },
        {
          id: 3,
          name: "Cardano",
          symbol: "ADA",
          priceTarget: 0.68,
          timeframe: "10 Days",
          trend: "Neutral",
          confidence: 71,
          fallback: true,
          reason: 'Bulk prediction failed'
        },
        {
          id: 4,
          name: "Solana",
          symbol: "SOL",
          priceTarget: 145,
          timeframe: "7 Days",
          trend: "Bullish",
          confidence: 79,
          fallback: true,
          reason: 'Bulk prediction failed'
        }
      ]);
    } finally {
      setLoadingRelated(false);
    }
  };

  // Enhanced manual retry function
  const handleRetryPrediction = async () => {
    setRetryAttempts(prev => prev + 1);
    setAuthenticationError(null);
    await loadAIPrediction();
    await loadAIRelatedPredictions();
  };

  // Enhanced diagnostic function
  const runDiagnostics = async () => {
    setAlertMessage('Running OpenAI API diagnostics...');
    setShowAlert(true);
    
    const keyValidation = validateApiKeyConfiguration();
    const authTest = await testOpenAIAuthentication();
    
    if (keyValidation?.isValid && authTest?.success) {
      setAlertMessage('✅ OpenAI API configuration is working correctly!');
    } else if (!keyValidation?.isValid) {
      setAlertMessage('❌ API key configuration issue detected. Please check your .env file.');
    } else {
      setAlertMessage(`❌ Authentication failed: ${authTest?.message}`);
    }
    
    setTimeout(() => setShowAlert(false), 8000);
  };

  const handleSetAlert = (prediction) => {
    setAlertMessage(`Price alert set for ${prediction?.name} at $${prediction?.priceTarget?.toLocaleString()}`);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleAddToWatchlist = (prediction) => {
    setAlertMessage(`${prediction?.name} added to your watchlist`);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleViewPrediction = (prediction) => {
    // In a real app, this would navigate to the specific prediction
    console.log("Viewing prediction for:", prediction?.name);
  };

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  // Enhanced loading state
  if (loading || loadingRelated) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          user={user} 
          title="AI Predictions"
          subtitle="Advanced cryptocurrency predictions powered by AI"
          onLogin={handleLogin}
          onLogout={handleLogout}
          onUpgrade={handleUpgrade}
        />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {loading ? 'Generating AI Predictions' : 'Loading Related Predictions'}
                </h3>
                <p className="text-muted-foreground">
                  {loading ? 'Analyzing market data with OpenAI...' : 'Generating AI insights for related cryptocurrencies...'}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user} 
        title="AI Predictions"
        subtitle="Advanced cryptocurrency predictions powered by AI"
        onLogin={handleLogin}
        onLogout={handleLogout}
        onUpgrade={handleUpgrade}
      />
      {/* Enhanced Alert Notification with API integration status */}
      {showAlert && (
        <div className={`fixed top-20 right-4 px-4 py-3 rounded-lg shadow-elevation-3 z-50 animate-fade-in max-w-sm ${
          alertMessage?.includes('quota') || alertMessage?.includes('failed') || alertMessage?.includes('Error') || alertMessage?.includes('exceeded') || alertMessage?.includes('authentication') || alertMessage?.includes('❌')
            ? 'bg-orange-50 border border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-200' : alertMessage?.includes('✅') || alertMessage?.includes('working')
              ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200' :'bg-success text-success-foreground'
        }`}>
          <div className="flex items-start space-x-3">
            <Icon 
              name={alertMessage?.includes('quota') || alertMessage?.includes('failed') || alertMessage?.includes('Error') || alertMessage?.includes('exceeded') || alertMessage?.includes('authentication') || alertMessage?.includes('❌')
                ? "AlertTriangle" 
                : alertMessage?.includes('✅') 
                  ? "CheckCircle2" :"CheckCircle"} 
              size={20} 
              className="mt-0.5 flex-shrink-0" 
            />
            <div className="flex-1">
              <span className="text-sm font-medium block">{alertMessage}</span>
              {(alertMessage?.includes('quota') || alertMessage?.includes('exceeded')) && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs opacity-80">
                    Your OpenAI account needs billing credits to continue using AI features.
                  </p>
                  <a 
                    href="https://platform.openai.com/account/billing" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs font-medium hover:underline"
                  >
                    <Icon name="ExternalLink" size={12} className="mr-1" />
                    Add Billing Credits
                  </a>
                </div>
              )}
              {(alertMessage?.includes('authentication') || alertMessage?.includes('invalid') || alertMessage?.includes('expired')) && (
                <div className="mt-2">
                  <a 
                    href="https://platform.openai.com/api-keys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs font-medium hover:underline"
                  >
                    <Icon name="ExternalLink" size={12} className="mr-1" />
                    Check API Key
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
            <button 
              onClick={() => window.location.href = '/trading-dashboard'}
              className="hover:text-foreground transition-standard"
            >
              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} />
            <button 
              onClick={() => window.location.href = '/ai-predictions-detail'}
              className="hover:text-foreground transition-standard"
            >
              AI Predictions
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">Bitcoin Analysis</span>
          </nav>

          {/* Enhanced API Configuration Status Panel */}
          {apiKeyStatus && (
            <div className="mb-6">
              {!apiKeyStatus?.isValid ? (
                <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-warning mb-2">OpenAI API Configuration Required</h4>
                      {apiKeyStatus?.isPlaceholder ? (
                        <div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Your OpenAI API key appears to be a placeholder. Please replace it with a real API key to enable AI predictions.
                          </p>
                          <div className="bg-muted/50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-muted-foreground mb-2">
                              <strong>Current key status:</strong> {apiKeyStatus?.keyPrefix}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              <strong>Required format:</strong> sk-proj-... (starts with "sk-")
                            </p>
                          </div>
                        </div>
                      ) : !apiKeyStatus?.hasKey ? (
                        <p className="text-sm text-muted-foreground mb-3">
                          No OpenAI API key found in your environment variables. Please add VITE_OPENAI_API_KEY to your .env file.
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground mb-3">
                          Invalid API key format detected. Please ensure your API key starts with "sk-" and is complete.
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-3">
                        <a 
                          href="https://platform.openai.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-warning hover:underline font-medium"
                        >
                          <Icon name="ExternalLink" size={14} className="mr-1" />
                          Get OpenAI API Key
                        </a>
                        <button
                          onClick={runDiagnostics}
                          className="inline-flex items-center text-sm px-3 py-1 bg-warning/10 hover:bg-warning/20 rounded-md transition-colors font-medium"
                        >
                          <Icon name="Stethoscope" size={14} className="mr-1" />
                          Run Diagnostics
                        </button>
                        <button
                          onClick={() => window.location?.reload()}
                          className="inline-flex items-center text-sm px-3 py-1 bg-primary/10 hover:bg-primary/20 rounded-md transition-colors font-medium"
                        >
                          <Icon name="RefreshCw" size={14} className="mr-1" />
                          Reload App
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800 dark:text-green-300 mb-1">OpenAI API Configured</h4>
                      <p className="text-sm text-green-700 dark:text-green-400 mb-2">
                        API key is properly configured and ready for use.
                      </p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-green-600 dark:text-green-400 font-mono bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                          {apiKeyStatus?.keyPrefix}
                        </span>
                        <button
                          onClick={runDiagnostics}
                          className="inline-flex items-center text-xs px-2 py-1 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded transition-colors font-medium text-green-700 dark:text-green-300"
                        >
                          <Icon name="Activity" size={12} className="mr-1" />
                          Test Connection
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhanced Authentication Error Panel */}
          {authenticationError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-800 dark:text-red-300 mb-1">
                    Authentication Failed
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                    {authenticationError?.message}
                  </p>
                  
                  {authenticationError?.suggestions && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">Troubleshooting steps:</p>
                      <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                        {authenticationError?.suggestions?.map((suggestion, index) => (
                          <li key={index} className="flex items-start">
                            <Icon name="ArrowRight" size={14} className="mr-1 mt-0.5 flex-shrink-0" />
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={handleRetryPrediction}
                      className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
                    >
                      <Icon name="RotateCcw" size={14} className="mr-1" />
                      Retry ({retryAttempts}/3)
                    </button>
                    {authenticationError?.status === 401 && (
                      <a 
                        href="https://platform.openai.com/api-keys" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
                      >
                        <Icon name="ExternalLink" size={14} className="mr-1" />
                        Fix API Key
                      </a>
                    )}
                    {authenticationError?.status === 429 && (
                      <a 
                        href="https://platform.openai.com/account/billing" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
                      >
                        <Icon name="ExternalLink" size={14} className="mr-1" />
                        Add Credits
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Fallback Mode Information */}
          {selectedPrediction?.fallback && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
                    {selectedPrediction?.errorType === 'RATE_LIMIT_EXCEEDED' ? 'API Quota Exceeded' : 
                     selectedPrediction?.errorType === 'AUTHENTICATION_ERROR'? 'Authentication Required' : selectedPrediction?.configurationRequired ?'Configuration Required': 'Fallback Mode Active'}
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                    {selectedPrediction?.errorType === 'RATE_LIMIT_EXCEEDED' ?'OpenAI API quota has been exceeded. Add billing credits to restore full AI functionality.' :
                      selectedPrediction?.errorType === 'AUTHENTICATION_ERROR' ?'OpenAI API authentication failed. Please check your API key configuration.' : 
                      selectedPrediction?.configurationRequired
                        ? 'OpenAI API key configuration is required for real-time AI predictions.' :
                        'AI predictions are currently using fallback data due to API limitations. The analysis shown is based on general market patterns and historical data.'
                    }
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button 
                      onClick={handleRetryPrediction}
                      className="inline-flex items-center text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors font-medium text-blue-700 dark:text-blue-300"
                    >
                      <Icon name="RotateCcw" size={14} className="mr-1" />
                      Try Again
                    </button>
                    {selectedPrediction?.errorType === 'RATE_LIMIT_EXCEEDED' && (
                      <a 
                        href="https://platform.openai.com/account/billing" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors font-medium text-blue-700 dark:text-blue-300"
                      >
                        <Icon name="ExternalLink" size={14} className="mr-1" />
                        Add Credits
                      </a>
                    )}
                    {(selectedPrediction?.errorType === 'AUTHENTICATION_ERROR' || selectedPrediction?.configurationRequired) && (
                      <a 
                        href="https://platform.openai.com/api-keys" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors font-medium text-blue-700 dark:text-blue-300"
                      >
                        <Icon name="Key" size={14} className="mr-1" />
                        Configure API Key
                      </a>
                    )}
                    <button
                      onClick={runDiagnostics}
                      className="inline-flex items-center text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors font-medium text-blue-700 dark:text-blue-300"
                    >
                      <Icon name="Stethoscope" size={14} className="mr-1" />
                      Diagnostics
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Integration Status */}
          {!selectedPrediction?.fallback && import.meta.env?.VITE_OPENAI_API_KEY && import.meta.env?.VITE_OPENAI_API_KEY !== 'your-openai-api-key-here' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800 dark:text-green-300 mb-1">AI Integration Active</h4>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Real-time AI predictions and analysis are being generated using OpenAI GPT-4. 
                    All data shown is powered by advanced machine learning algorithms.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="xl:col-span-2 space-y-8">
              {selectedPrediction && (
                <PredictionCard 
                  prediction={selectedPrediction}
                  onAddToWatchlist={handleAddToWatchlist}
                  onSetAlert={handleSetAlert}
                />
              )}
              
              <PredictionChart 
                chartData={chartData}
                predictions={predictions}
              />
              
              <AccuracyStats 
                accuracyData={accuracyData}
                performanceMetrics={performanceMetrics}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              <MarketAnalysis analysisData={analysisData} />
              
              <RelatedPredictions 
                predictions={relatedPredictions}
                onViewPrediction={handleViewPrediction}
              />
            </div>
          </div>

          {/* Action Bar */}
          <div className="mt-12 bg-card border border-border rounded-lg p-6 shadow-elevation-2">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-3">
                <Icon name="Lightbulb" size={24} className="text-primary" />
                <div>
                  <h4 className="font-semibold text-card-foreground">Ready to act on this prediction?</h4>
                  <p className="text-sm text-muted-foreground">
                    Set up automated alerts or add to your portfolio tracker
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  icon="Plus"
                  iconName="Plus" 
                  iconPosition="left"
                  onClick={() => window.location.href = '/portfolio-tracker'}
                >
                  Add to Portfolio
                </Button>
                <Button 
                  variant="default" 
                  icon="Zap"
                  iconName="Zap" 
                  iconPosition="left"
                  onClick={() => window.location.href = '/trading-signals'}
                >
                  View Trading Signals
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIPredictionsDetail;